import type { SoundEventKey, SoundSetting } from "@loot-os/api-contract";
import { isSoundContentType, resolveStorefrontSounds } from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorAlert } from "../components/ErrorAlert";
import { SoundList } from "../components/sounds/SoundList";
import { SoundListSkeleton } from "../components/sounds/SoundListSkeleton";
import { uploadTenantAssetFile } from "../lib/tenantAssets";
import { useTRPC } from "../lib/trpc";

const SOUND_KIND = { accept: isSoundContentType, acceptLabel: "only MP3 files are allowed" };

/**
 * The platform default sound per box opener event: what every storefront plays until its tenant
 * sets one under Storefront → Sounds. An event with no default is silent (a battle event plays its
 * opener twin's). Clear removes a default, which leaves the event silent again.
 * Platform admins only.
 */
export function PlatformSoundsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.sounds.get.queryOptions();
  const query = useQuery(options);
  const set = useMutation(
    trpc.platform.sounds.set.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(options.queryKey, saved);
        void queryClient.invalidateQueries({ queryKey: options.queryKey });
        toast.success("Sound saved.");
      },
    }),
  );
  const startUpload = useMutation(trpc.platform.sounds.startUpload.mutationOptions());
  const finishUpload = useMutation(trpc.platform.sounds.finishUpload.mutationOptions());

  const own = query.data?.sounds;

  const onSet = async (event: SoundEventKey, setting: SoundSetting | null) => {
    await set.mutateAsync({ event, setting });
  };
  const onUpload = async (event: SoundEventKey, file: File) => {
    const { publicUrl } = await uploadTenantAssetFile(
      file,
      (input) => startUpload.mutateAsync(input),
      (uploadKey) => finishUpload.mutateAsync({ uploadKey }),
      SOUND_KIND,
    );
    // A file replaces the sound but keeps its trim.
    const current = own?.[event];
    const gainDb = current && "url" in current ? current.gainDb : undefined;
    await onSet(event, gainDb === undefined ? { url: publicUrl } : { url: publicUrl, gainDb });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Sounds</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The sound every storefront's openers play for each event. A tenant keeps these until it
          sets its own under Storefront → Sounds. An event with none is silent, and a battle event
          with none plays the matching Box opening sound.
        </p>
      </div>
      {query.error && <ErrorAlert message={query.error.message} />}
      {query.isLoading && <SoundListSkeleton />}
      {own && (
        <SoundList
          scope="platform"
          sounds={{ own, resolved: resolveStorefrontSounds(own, {}) }}
          canEdit
          onUpload={onUpload}
          onSet={onSet}
        />
      )}
    </div>
  );
}
